import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Plus, Trash2, GripVertical, Check, ArrowRight, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StageSettingsPanel({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [transitions, setTransitions] = useState({});
  const [activeTab, setActiveTab] = useState('stages');
  const [newStageLabel, setNewStageLabel] = useState('');
  const [newStageCategory, setNewStageCategory] = useState('lead');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/stage-settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStages(data.stages || []);
        setTransitions(data.transitions || {});
      }
    } catch (err) {
      logger.error('Failed to load stage settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchSettings();
  }, [open, fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/stage-settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages, transitions }),
      });
      if (res.ok) {
        toast.success('Stage settings saved');
        onOpenChange(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addStage = async () => {
    if (!newStageLabel.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/stage-settings/stages`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newStageLabel.trim(), category: newStageCategory }),
      });
      if (res.ok) {
        const data = await res.json();
        setStages(prev => [...prev, data.stage]);
        setTransitions(prev => ({ ...prev, [data.stage.stage_id]: [] }));
        setNewStageLabel('');
        toast.success(`Stage "${data.stage.label}" added`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add stage');
      }
    } catch {
      toast.error('Failed to add stage');
    }
  };

  const removeStage = async (stageId) => {
    try {
      const res = await fetch(`${API_URL}/api/stage-settings/stages/${stageId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setStages(prev => prev.filter(s => s.stage_id !== stageId));
        setTransitions(prev => {
          const next = { ...prev };
          delete next[stageId];
          for (const key of Object.keys(next)) {
            next[key] = next[key].filter(t => t !== stageId);
          }
          return next;
        });
        toast.success('Stage removed');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to remove');
      }
    } catch {
      toast.error('Failed to remove stage');
    }
  };

  const updateStageField = (stageId, field, value) => {
    setStages(prev => prev.map(s => s.stage_id === stageId ? { ...s, [field]: value } : s));
  };

  const toggleTransition = (fromStage, toStage) => {
    setTransitions(prev => {
      const current = prev[fromStage] || [];
      const next = current.includes(toStage)
        ? current.filter(t => t !== toStage)
        : [...current, toStage];
      return { ...prev, [fromStage]: next };
    });
  };

  const moveStage = (idx, direction) => {
    setStages(prev => {
      const next = [...prev];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const leadStages = stages.filter(s => s.category === 'lead');
  const oppStages = stages.filter(s => s.category === 'opportunity');

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="stage-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 size={20} />
            Lead Stage Settings
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-4">
          <button onClick={() => setActiveTab('stages')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${activeTab === 'stages' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-stages">
            Stages & Categories
          </button>
          <button onClick={() => setActiveTab('transitions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all ${activeTab === 'transitions' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-transitions">
            Movement Rules
          </button>
        </div>

        {activeTab === 'stages' && (
          <div className="space-y-6">
            {/* Lead Stages */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800">Lead</Badge>
                Lead Stages ({leadStages.length})
              </h3>
              <div className="space-y-2">
                {leadStages.map((stage, idx) => {
                  const globalIdx = stages.findIndex(s => s.stage_id === stage.stage_id);
                  return (
                    <div key={stage.stage_id} className="flex items-center gap-2 p-2 border rounded-lg bg-blue-50/30" data-testid={`stage-row-${stage.stage_id}`}>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveStage(globalIdx, -1)} className="text-slate-400 hover:text-slate-600 text-xs leading-none">&uarr;</button>
                        <button onClick={() => moveStage(globalIdx, 1)} className="text-slate-400 hover:text-slate-600 text-xs leading-none">&darr;</button>
                      </div>
                      <Input value={stage.label} onChange={(e) => updateStageField(stage.stage_id, 'label', e.target.value)}
                        className="flex-1 h-8 text-sm" data-testid={`stage-label-${stage.stage_id}`} />
                      <Select value={stage.category} onValueChange={(v) => updateStageField(stage.stage_id, 'category', v)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="opportunity">Opportunity</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                        <input type="checkbox" checked={stage.is_terminal || false}
                          onChange={(e) => updateStageField(stage.stage_id, 'is_terminal', e.target.checked)}
                          className="rounded border-slate-300 h-3.5 w-3.5" />
                        Terminal
                      </label>
                      {stage.is_custom && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                          onClick={() => removeStage(stage.stage_id)} title="Remove stage">
                          <Trash2 size={14} />
                        </Button>
                      )}
                      {!stage.is_custom && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-300 hover:text-red-500"
                          onClick={() => removeStage(stage.stage_id)} title="Remove stage (blocked if leads exist)">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Opportunity Stages */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-800">Opportunity</Badge>
                Opportunity Stages ({oppStages.length})
              </h3>
              <p className="text-xs text-slate-500 mb-2">Moving a lead from a Lead stage to an Opportunity stage will require Deal Value, Estimated Cash Collection, and Product Details.</p>
              <div className="space-y-2">
                {oppStages.map((stage) => {
                  const globalIdx = stages.findIndex(s => s.stage_id === stage.stage_id);
                  return (
                    <div key={stage.stage_id} className="flex items-center gap-2 p-2 border rounded-lg bg-purple-50/30" data-testid={`stage-row-${stage.stage_id}`}>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveStage(globalIdx, -1)} className="text-slate-400 hover:text-slate-600 text-xs leading-none">&uarr;</button>
                        <button onClick={() => moveStage(globalIdx, 1)} className="text-slate-400 hover:text-slate-600 text-xs leading-none">&darr;</button>
                      </div>
                      <Input value={stage.label} onChange={(e) => updateStageField(stage.stage_id, 'label', e.target.value)}
                        className="flex-1 h-8 text-sm" data-testid={`stage-label-${stage.stage_id}`} />
                      <Select value={stage.category} onValueChange={(v) => updateStageField(stage.stage_id, 'category', v)}>
                        <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="opportunity">Opportunity</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                        <input type="checkbox" checked={stage.is_terminal || false}
                          onChange={(e) => updateStageField(stage.stage_id, 'is_terminal', e.target.checked)}
                          className="rounded border-slate-300 h-3.5 w-3.5" />
                        Terminal
                      </label>
                      {stage.is_custom && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                          onClick={() => removeStage(stage.stage_id)} title="Remove stage">
                          <Trash2 size={14} />
                        </Button>
                      )}
                      {!stage.is_custom && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-300 hover:text-red-500"
                          onClick={() => removeStage(stage.stage_id)} title="Remove stage (blocked if leads exist)">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Add New Stage */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Add New Stage</Label>
              <div className="flex gap-2 mt-2">
                <Input value={newStageLabel} onChange={(e) => setNewStageLabel(e.target.value)}
                  placeholder="Stage name..." className="flex-1" data-testid="new-stage-input" />
                <Select value={newStageCategory} onValueChange={setNewStageCategory}>
                  <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="opportunity">Opportunity</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={addStage} disabled={!newStageLabel.trim()} data-testid="add-stage-btn">
                  <Plus size={16} className="mr-1" /> Add
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transitions' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">For each stage, select which stages a lead can move TO. Check the boxes to allow transitions.</p>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600 sticky left-0 bg-slate-50 min-w-[160px]">From Stage</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600" colSpan={stages.length}>
                      Can Move To &rarr;
                    </th>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <th className="sticky left-0 bg-slate-50/50"></th>
                    {stages.map(s => (
                      <th key={s.stage_id} className="px-1 py-1 text-center font-normal text-[10px] text-slate-500 max-w-[60px]" title={s.label}>
                        <div className="truncate">{s.label.split(' ').map(w => w[0]).join('')}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stages.map(fromStage => (
                    <tr key={fromStage.stage_id} className="border-t hover:bg-slate-50/50" data-testid={`transition-row-${fromStage.stage_id}`}>
                      <td className="px-3 py-2 font-medium text-slate-700 sticky left-0 bg-white text-xs whitespace-nowrap">
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${fromStage.category === 'opportunity' ? 'bg-purple-400' : 'bg-blue-400'}`}></span>
                        {fromStage.label}
                      </td>
                      {stages.map(toStage => {
                        const isSelf = fromStage.stage_id === toStage.stage_id;
                        const isAllowed = (transitions[fromStage.stage_id] || []).includes(toStage.stage_id);
                        return (
                          <td key={toStage.stage_id} className="px-1 py-1 text-center">
                            {isSelf ? (
                              <span className="text-slate-300">-</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isAllowed}
                                onChange={() => toggleTransition(fromStage.stage_id, toStage.stage_id)}
                                className="rounded border-slate-300 h-3.5 w-3.5 text-blue-600 cursor-pointer"
                                title={`${fromStage.label} → ${toStage.label}`}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">Tip: Moving from a <span className="text-blue-600 font-medium">Lead</span> stage to an <span className="text-purple-600 font-medium">Opportunity</span> stage will prompt the user to fill Deal Value, Estimated Cash Collection, and Product Details.</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-stage-settings">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
            Save All Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
